/*
 * newsbot - Real-time logs viewer
 * Adapted from autobrr (GPL-2.0-or-later)
 */

import { Fragment, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from "@headlessui/react";

import { useDebounce } from "@hooks/hooks";
import {
  Cog6ToothIcon,
  DocumentArrowDownIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { ExclamationCircleIcon } from "@heroicons/react/24/solid";
import { format } from "date-fns/format";

import { Checkbox } from "@components/Checkbox";
import { classNames } from "@utils";
import { EmptyListState } from "@components/emptystates";
import { toast } from "@components/hot-toast";
import Toast from "@components/notifications/Toast";
import { RingResizeSpinner } from "@components/Icons";
import { api } from "@api/client";
import { parseSseChunk, updateEventBuffer } from "@utils/sse";

type LogEvent = {
  time: string;
  level: string;
  logger: string;
  message: string;
};

type LogLevel = "TRC" | "DBG" | "INF" | "ERR" | "WRN" | "FTL" | "PNC";
type ConnectionState = "connecting" | "live" | "reconnecting" | "failed";

const MAX_LOGS = 1_000;
const RECONNECT_DELAY_MS = 2_000;

const LogColors: Record<LogLevel, string> = {
  "TRC": "text-purple-300",
  "DBG": "text-yellow-500",
  "INF": "text-green-500",
  "ERR": "text-red-500",
  "WRN": "text-yellow-500",
  "FTL": "text-red-500",
  "PNC": "text-red-600",
};

interface LogSettings {
  scrollOnNewLog: boolean;
  indentLogLines: boolean;
  hideWrappedText: boolean;
}

const defaultSettings: LogSettings = {
  scrollOnNewLog: true,
  indentLogLines: false,
  hideWrappedText: false,
};

export const Logs = () => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [settings, setSettings] = useState<LogSettings>(() => {
    try {
      const saved = localStorage.getItem("logSettings");
      return saved ? { ...defaultSettings, ...JSON.parse(saved) } : defaultSettings;
    } catch {
      return defaultSettings;
    }
  });
  const [logs, setLogs] = useState<LogEvent[]>([]);
  const [connectionState, setConnectionState] = useState<ConnectionState>("connecting");
  const [searchInput, setSearchInput] = useState("");
  const searchFilter = useDebounce(searchInput, 200);
  const [filteredLogs, setFilteredLogs] = useState<LogEvent[]>([]);
  const [isInvalidRegex, setIsInvalidRegex] = useState(false);

  useEffect(() => {
    localStorage.setItem("logSettings", JSON.stringify(settings));
  }, [settings]);

  // Auto-scroll to bottom
  useEffect(() => {
    const scrollToBottom = () => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
      }
    };
    if (settings.scrollOnNewLog) scrollToBottom();
  }, [filteredLogs, settings.scrollOnNewLog]);

  useEffect(() => {
    const controller = new AbortController();
    let attempted = false;

    const connect = async () => {
      while (!controller.signal.aborted) {
        if (attempted) setConnectionState("reconnecting");
        attempted = true;
        let failed = false;
        let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;

        try {
          const response = await api.raw("/logs/stream", { signal: controller.signal });
          reader = response.body?.getReader();
          if (!reader) throw new Error("Log stream response has no body");

          setConnectionState("live");
          const decoder = new TextDecoder();
          let remainder = "";
          let replaceOnNextEvents = true;

          while (!controller.signal.aborted) {
            const { done, value } = await reader.read();
            if (done) break;

            const parsed = parseSseChunk(remainder, decoder.decode(value, { stream: true }));
            remainder = parsed.remainder;
            const entries = parsed.events.flatMap((payload) => {
              try {
                return [JSON.parse(payload) as LogEvent];
              } catch (error) {
                console.error("Invalid log stream event", error);
                return [];
              }
            });
            if (entries.length > 0) {
              setLogs((current) => updateEventBuffer(current, entries, replaceOnNextEvents, MAX_LOGS));
              replaceOnNextEvents = false;
            }
          }
        } catch (error) {
          if (controller.signal.aborted) return;
          failed = true;
          setConnectionState("failed");
          console.error("Log stream connection failed", error);
        } finally {
          if (reader) {
            await reader.cancel().catch(() => undefined);
            reader.releaseLock();
          }
        }

        if (!failed) setConnectionState("reconnecting");
        await new Promise((resolve) => setTimeout(resolve, RECONNECT_DELAY_MS));
      }
    };

    void connect();
    return () => controller.abort();
  }, []);

  // Filter logs based on search pattern
  useEffect(() => {
    if (!searchFilter.length) {
      setFilteredLogs(logs);
      setIsInvalidRegex(false);
      return;
    }

    try {
      const pattern = new RegExp(searchFilter, "i");
      const newLogs = logs.filter((log) => pattern.test(log.message) || pattern.test(log.logger));
      setFilteredLogs(newLogs);
      setIsInvalidRegex(false);
    } catch {
      setFilteredLogs([]);
      setIsInvalidRegex(true);
    }
  }, [logs, searchFilter]);

  const handleClearLogs = () => {
    setLogs([]);
    toast.custom((t) => <Toast type="success" body="Logs cleared from view." t={t} />);
  };

  const onSetValue = (key: keyof LogSettings, newValue: boolean) =>
    setSettings((prevState) => ({
      ...prevState,
      [key]: newValue,
    }));

  const connectionMessage = connectionState === "live"
    ? "Live"
    : connectionState === "connecting"
      ? "Connecting…"
      : connectionState === "failed"
        ? "Connection failed — retrying…"
        : "Reconnecting…";

  return (
    <main>
      <div className="my-6 max-w-(--breakpoint-xl) mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-black dark:text-white">Logs</h1>
      </div>

      <div className="max-w-(--breakpoint-xl) mx-auto pb-12 px-2 sm:px-4 lg:px-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-250 dark:border-gray-775 px-2 sm:px-4 pt-3 sm:pt-4 pb-3 sm:pb-4">
          <div className="flex relative mb-3">
            <input
              type="text"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              className={classNames(
                "focus:ring-blue-500 dark:focus:ring-blue-500 focus:border-blue-500 dark:focus:border-blue-500 border-gray-300 dark:border-gray-700",
                "block w-full dark:bg-gray-900 shadow-sm dark:text-gray-100 sm:text-sm rounded-md"
              )}
              placeholder="Enter a regex pattern to filter logs by..."
            />
            {isInvalidRegex && (
              <div className="absolute mt-1.5 right-28 items-center text-xs text-red-500">
                <ExclamationCircleIcon className="h-6 w-6 inline mr-1" />
              </div>
            )}
            <button
              type="button"
              onClick={handleClearLogs}
              className="px-4 py-2"
              title="Clear logs from view"
            >
              <TrashIcon className="w-5 h-5 text-gray-700 hover:text-gray-900 dark:text-gray-100 dark:hover:text-gray-400" aria-hidden="true" />
            </button>
            <LogsDropdown settings={settings} onSetValue={onSetValue} />
          </div>

          <p role="status" className="mb-2 text-xs text-gray-500 dark:text-gray-400">
            {connectionMessage}
          </p>

          <div
            className="overflow-y-auto overflow-x-auto px-2 rounded-lg h-[60vh] min-w-full bg-gray-100 dark:bg-gray-900 font-mono text-sm"
            ref={messagesEndRef}
          >
            {filteredLogs.length === 0 ? (
              <div className="flex items-center justify-center h-full text-gray-500">
                {logs.length === 0 ? connectionMessage : "No matching logs"}
              </div>
            ) : (
              filteredLogs.map((entry, idx) => (
                <div
                  key={idx}
                  className={classNames(
                    settings.indentLogLines ? "grid justify-start grid-flow-col" : "",
                    settings.hideWrappedText ? "truncate hover:text-ellipsis hover:whitespace-normal" : "whitespace-nowrap"
                  )}
                >
                  <span
                    title={entry.time}
                    className="text-gray-500 dark:text-gray-600"
                  >
                    {format(new Date(entry.time), "HH:mm:ss")}
                  </span>
                  {entry.level in LogColors ? (
                    <span
                      className={classNames(
                        LogColors[entry.level as LogLevel],
                        "font-semibold"
                      )}
                    >
                      {` ${entry.level} `}
                    </span>
                  ) : (
                    <span className="font-semibold text-gray-400">
                      {` ${entry.level} `}
                    </span>
                  )}
                  <span className="text-gray-600 dark:text-gray-400">
                    [{entry.logger.split(".").pop()}]
                  </span>
                  <span className="text-black dark:text-gray-300 ml-1">
                    {entry.message}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="max-w-(--breakpoint-xl) mx-auto pb-10 px-2 sm:px-4 lg:px-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-250 dark:border-gray-775 px-4 sm:px-6 pt-3 sm:pt-4">
          <LogFiles />
        </div>
      </div>
    </main>
  );
};

interface LogFile {
  filename: string;
  size: string;
  updated_at: string;
}

interface LogFilesResponse {
  files: LogFile[];
}

export const LogFiles = () => {
  const { isLoading, isError, error, data } = useQuery({
    queryKey: ["log-files"],
    queryFn: () => api.get<LogFilesResponse>("/logs/files"),
    retry: false,
    refetchOnWindowFocus: false,
  });

  if (isLoading) {
    return (
      <div className="py-4">
        <EmptyListState text="Loading log files..." />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="py-4 text-sm text-red-600 dark:text-red-400" role="alert">
        Could not load log files: {error.message}
      </div>
    );
  }

  return (
    <div>
      <div className="mt-2">
        <h2 className="text-lg leading-4 font-bold text-gray-900 dark:text-white">Log files</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Download log files for debugging.
        </p>
      </div>

      {data && data.files && data.files.length > 0 ? (
        <ul className="py-3 min-w-full relative">
          <li className="grid grid-cols-12 mb-2 border-b border-gray-200 dark:border-gray-700">
            <div className="hidden sm:block col-span-5 px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Name
            </div>
            <div className="col-span-8 sm:col-span-4 px-1 sm:px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Last modified
            </div>
            <div className="col-span-3 sm:col-span-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Size
            </div>
          </li>

          {data.files.map((f, idx) => (
            <LogFilesItem key={idx} file={f} />
          ))}
        </ul>
      ) : (
        <div className="py-4">
          <EmptyListState text="No log files available" />
        </div>
      )}
    </div>
  );
};

interface LogFilesItemProps {
  file: LogFile;
}

const LogFilesItem = ({ file }: LogFilesItemProps) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    setIsDownloading(true);

    const toastId = toast.custom((t) => (
      <Toast type="info" body="Downloading log file..." t={t} />
    ));

    try {
      const response = await api.raw(`/logs/files/${encodeURIComponent(file.filename)}`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.custom((t) => (
        <Toast
          type="error"
          body={error instanceof Error ? error.message : "Log download failed"}
          t={t}
        />
      ));
    } finally {
      toast.dismiss(toastId);
      setIsDownloading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    try {
      return format(new Date(dateStr), "yyyy-MM-dd HH:mm:ss");
    } catch {
      return dateStr;
    }
  };

  return (
    <li className="text-gray-500 dark:text-gray-400">
      <div className="grid grid-cols-12 items-center py-2">
        <div className="col-span-4 sm:col-span-5 px-2 py-0 truncate hidden sm:block sm:text-sm text-md font-medium text-gray-900 dark:text-gray-200">
          <div className="block truncate justify-between">{file.filename}</div>
        </div>
        <div
          className="col-span-8 sm:col-span-4 block truncate px-1 sm:px-2 items-center text-sm font-medium text-gray-900 dark:text-gray-200"
          title={file.updated_at}
        >
          {formatDate(file.updated_at)}
        </div>
        <div className="col-span-3 sm:col-span-2 flex items-center text-sm font-small sm:font-medium text-gray-900 dark:text-gray-200">
          {file.size}
        </div>
        <div className="col-span-1 sm:col-span-1 pl-0 flex items-center justify-center text-sm font-medium text-gray-900 dark:text-white">
          <button
            type="button"
            className={classNames(
              "text-gray-900 dark:text-gray-300",
              "font-medium group flex rounded-md items-center px-2 py-2 text-sm"
            )}
            title={!isDownloading ? "Download file" : "Downloading..."}
            onClick={handleDownload}
            disabled={isDownloading}
            aria-label={isDownloading ? "Downloading log file" : `Download ${file.filename}`}
          >
            {!isDownloading ? (
              <DocumentArrowDownIcon
                className="text-blue-500 w-5 h-5"
                aria-hidden="true"
              />
            ) : (
              <RingResizeSpinner className="text-blue-500 w-5 h-5" />
            )}
          </button>
        </div>
      </div>
    </li>
  );
};

interface LogsDropdownProps {
  settings: LogSettings;
  onSetValue: (key: keyof LogSettings, newValue: boolean) => void;
}

const LogsDropdown = ({ settings, onSetValue }: LogsDropdownProps) => {
  return (
    <Menu as="div">
      <MenuButton className="px-4 py-2">
        <Cog6ToothIcon
          className="w-5 h-5 text-gray-700 hover:text-gray-900 dark:text-gray-100 dark:hover:text-gray-400"
          aria-hidden="true"
        />
      </MenuButton>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <MenuItems className="absolute right-0 mt-1 origin-top-right bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700 rounded-md shadow-lg ring-1 ring-black ring-opacity-10 focus:outline-hidden z-10">
          <div className="p-3">
            <MenuItem>
              {() => (
                <Checkbox
                  label="Scroll to bottom on new message"
                  value={settings.scrollOnNewLog}
                  setValue={(newValue) => onSetValue("scrollOnNewLog", newValue)}
                />
              )}
            </MenuItem>
            <MenuItem>
              {() => (
                <Checkbox
                  label="Indent log lines"
                  description="Indent each log line according to their position."
                  value={settings.indentLogLines}
                  setValue={(newValue) => onSetValue("indentLogLines", newValue)}
                />
              )}
            </MenuItem>
            <MenuItem>
              {() => (
                <Checkbox
                  label="Hide wrapped text"
                  description="Truncate long lines (hover to expand)."
                  value={settings.hideWrappedText}
                  setValue={(newValue) => onSetValue("hideWrappedText", newValue)}
                />
              )}
            </MenuItem>
          </div>
        </MenuItems>
      </Transition>
    </Menu>
  );
};

export default Logs;
